import {
	ASSETS_MULTI_SITE_URL,
	ASSETS_UNITS_ENABLED_ERROR_TTL_MS,
	ASSETS_UNITS_ENABLED_TTL_MS,
} from '../common/constants';
import type { MultiSiteResponse, UnitsEnabledCheck } from '../common/types';
import {
	getUnitsEnabled,
	getUnitsInFlight,
	isUnitsEnabledStale,
	setUnitsEnabled,
	setUnitsInFlight,
} from '../state/store';

const performFetch = async (cloudId: string): Promise<UnitsEnabledCheck> => {
	try {
		const response = await fetch(ASSETS_MULTI_SITE_URL, {
			method: 'GET',
			credentials: 'include',
			headers: { accept: 'application/json', 'x-atlassian-cloud-id': cloudId },
		});
		if (!response.ok) {
			throw new Error(`Assets multi-site check returned ${response.status}`);
		}
		const parsed = (await response.json()) as MultiSiteResponse;
		const enabled = parsed?.isUnitsEnabledForAssets === true;
		const multiSite = typeof parsed?.multiSite === 'boolean' ? parsed.multiSite : null;
		setUnitsEnabled(cloudId, enabled, ASSETS_UNITS_ENABLED_TTL_MS, multiSite);
		return { isUnitsEnabledForAssets: enabled };
	} catch {
		// Fail closed: treat Units as off, and retry after a short TTL.
		setUnitsEnabled(cloudId, false, ASSETS_UNITS_ENABLED_ERROR_TTL_MS);
		return { isUnitsEnabledForAssets: false };
	}
};

// Single-flight, TTL-cached Units check; never rejects, fails closed to `false`.
export const fetchIsUnitsEnabledForAssets = async (
	cloudId: string,
	force: boolean = false,
): Promise<UnitsEnabledCheck> => {
	if (!force) {
		const inFlight = getUnitsInFlight(cloudId);
		if (inFlight) {
			return inFlight;
		}
		const cached = getUnitsEnabled(cloudId);
		if (cached !== null && !isUnitsEnabledStale(cloudId)) {
			return { isUnitsEnabledForAssets: cached };
		}
	}
	const promise = performFetch(cloudId);
	setUnitsInFlight(cloudId, promise);
	return promise;
};
